import type { ActiveCredit } from '../../types/billing';

/**
 * ONE definition of the LiveAvatar minutes allowance, shared by every surface
 * that renders it. Owner decision 2026-08-20, option (a): show
 * `used / (tier + purchased)` as a SINGLE combined bar, never a second row and
 * never an over-100% state.
 *
 * 🚨 WHY THIS EXISTS AT ALL. Backend `back a04e767` made purchased bundles
 * EXTEND the cap (tier minutes burn first, bundle credits second), but every
 * frontend avatar display still divided by a tier-only number. A customer who
 * used 60 tier + 12 bundle minutes saw "72 / 60" — an overflowing bar that
 * reads as our counting being broken, shown precisely to the customers who paid
 * us extra. Three surfaces rendered that number three different ways, which is
 * why the arithmetic now lives here instead of in each of them.
 *
 * ── THE PURCHASED TERM IS `amount_initial`, NOT `amount` ────────────────────
 * 🚨 This is the one decision most likely to be "corrected" back into a bug.
 * `amount` is the REMAINING balance on a credit row, and using it makes the
 * denominator shrink by exactly as much as the numerator grows:
 *
 *     tier 60, one 50-min bundle, 100 minutes used
 *       remaining → 60 + 10 = 70   ⇒ "100 / 70"   ← OVERFLOWS, the original bug
 *       initial   → 60 + 50 = 110  ⇒ "100 / 110"  ← correct
 *
 * `amount_initial` is what the customer BOUGHT, and the size of the allowance
 * is what a denominator means. `back/utils/plan_guards.py::
 * liveavatar_total_bundle_minutes` sums `amount` because it answers a different
 * question — how much headroom is LEFT — and that number belongs on the
 * remaining/expiry copy, never under a division bar.
 *
 * ── KNOWN HOLE, MITIGATED BUT NOT FIXED HERE ───────────────────────────────
 * 🔴 `GET /api/billing/credits` returns only `status: "active"` rows, and
 * `usage_service._decrement_credit_row` flips a row to `"exhausted"` the moment
 * it hits exactly 0. So a FULLY DRAINED bundle vanishes from the response, its
 * `amount_initial` vanishes with it, and the combined cap collapses back to the
 * tier — reproducing "110 / 60" at the exact moment the customer has spent
 * everything they paid for. No client-side sum can see a row the API omits.
 *
 * `displayCap` therefore clamps up to `used`, which makes an over-100% state
 * unreachable by construction (the owner's requirement) and happens to render
 * the truthful "110 / 110" in the case that triggers it.
 *
 * 🚨 THE CLAMP IS PERMANENT — DO NOT REMOVE IT NOW THAT THE SERVER SENDS THE
 * TOTAL. `back bc75d88` shipped `minutes_cap_total` and documented two shapes
 * where `used` can still exceed it BY DESIGN: a bundle drained across a cycle
 * boundary (the whole drained portion is attributed to this cycle; exact
 * attribution would mean parsing `consumed_audit` on every page load), and
 * forfeit-and-resubscribe inside one cycle. Storm's commit asks for this clamp
 * to stay as the floor. It is no longer standing in for a missing field — it is
 * the floor under a denominator that is exact for the common shapes and
 * deliberately approximate for two rare ones.
 *
 * ── UNLIMITED ──────────────────────────────────────────────────────────────
 * 🚨 A `-1` cap is a sentinel, not a number: `-1 + 50 = 49` would render a
 * 49-minute cap for an unlimited workspace. `unlimited` short-circuits before
 * any arithmetic and every consumer must branch on it first.
 */
export interface AvatarMinutes {
  /** Admin override granted unlimited minutes — render "∞", never a number. */
  unlimited: boolean;
  /** Tier minutes included with the add-on, before any purchase. */
  tierCap: number;
  /** Purchased minutes (sum of `amount_initial` over ACTIVE bundle rows). */
  purchased: number;
  /** `tierCap + purchased` — the allowance as a fact, un-clamped. */
  totalCap: number;
  /** What to divide by. `totalCap`, floored at `used`; see the hole above. */
  displayCap: number;
  /** Purchased minutes exist — gates the "includes N purchased minutes" line. */
  hasPurchased: boolean;
  /** 0–100, never above. */
  pct: number;
}

export function resolveAvatarMinutes(opts: {
  /** `liveavatar_addon.minutes_cap_total` — the SERVER's combined allowance.
   *  Preferred over every client derivation when present; `-1` = unlimited.
   *  🚨 Already includes purchased minutes — nothing is added to it. */
  serverCapTotal?: number | null;
  /** `liveavatar_addon.minutes_cap` — the effective tier cap. */
  addonMinutesCap?: number | null;
  /** `usage_limits.max_liveavatar_minutes`. -1 = unlimited (admin override). */
  planLimit?: number | null;
  /** Active credit rows for `liveavatar_minutes` ONLY — caller pre-filters.
   *  Used only by the fallback path. */
  credits: ActiveCredit[];
  used: number;
}): AvatarMinutes {
  const { serverCapTotal, addonMinutesCap, planLimit, credits, used } = opts;

  // ── Preferred path: the server states the allowance it enforced ──────────
  // `back bc75d88`. Feature-detected on the FIELD, not on a flag, so an older
  // backend simply falls through to the client sum below and renders exactly
  // what it rendered before — neither deploy order breaks.
  if (typeof serverCapTotal === 'number') {
    if (serverCapTotal < 0) {
      return {
        unlimited: true, tierCap: -1, purchased: 0, totalCap: -1,
        displayCap: -1, hasPurchased: false, pct: 0,
      };
    }
    const tierCap = Math.max(0, addonMinutesCap ?? 0);
    // Derived, never added: the server total ALREADY contains the bundle, so
    // this is only for the "includes N purchased minutes" line. Floored at 0 —
    // an admin override of 0 reports a 0 total even with minutes on the books
    // (a hard block credits cannot bypass), and a negative difference there
    // would otherwise advertise purchased minutes that cannot be spent.
    const purchased = Math.max(0, serverCapTotal - tierCap);
    const displayCap = Math.max(serverCapTotal, used);
    return {
      unlimited: false,
      tierCap,
      purchased,
      totalCap: serverCapTotal,
      displayCap,
      hasPurchased: purchased > 0,
      pct: displayCap > 0 ? Math.min(100, (used / displayCap) * 100) : 0,
    };
  }

  if (typeof planLimit === 'number' && planLimit < 0) {
    return {
      unlimited: true,
      tierCap: -1,
      purchased: 0,
      totalCap: -1,
      displayCap: -1,
      hasPurchased: false,
      pct: 0,
    };
  }

  // 🚨 The add-on's tier minutes are the allowance the GATE enforces —
  // `back a04e767` moved the avatar base allowance onto the effective TIER cap
  // and OFF `max_liveavatar_minutes`, which is 0 on every App plan (only
  // Shopify Business sets 60). Dividing by the plan limit therefore produced a
  // second, different overflow on App plans: a workspace with a 50-minute
  // bundle and no plan quota rendered "72 / 50". The plan limit survives only
  // as the fallback for a workspace with no add-on payload at all.
  const tierCap =
    typeof addonMinutesCap === 'number' && addonMinutesCap > 0
      ? addonMinutesCap
      : Math.max(0, planLimit ?? 0);

  let purchased = 0;
  for (const c of credits) {
    const initial = Number(c.amount_initial);
    if (Number.isFinite(initial) && initial > 0) purchased += initial;
  }

  const totalCap = tierCap + purchased;
  const displayCap = Math.max(totalCap, used);
  const pct = displayCap > 0 ? Math.min(100, (used / displayCap) * 100) : 0;

  return {
    unlimited: false,
    tierCap,
    purchased,
    totalCap,
    displayCap,
    hasPurchased: purchased > 0,
    pct,
  };
}

/** The sub-line under the bar. `null` when nothing was purchased — the
 *  zero-bundle customer's view must stay exactly as it was. */
export function purchasedMinutesNote(m: AvatarMinutes): string | null {
  if (m.unlimited || !m.hasPurchased) return null;
  return `includes ${m.purchased.toLocaleString()} purchased minutes`;
}
